import config from '../config/environment'

const PAGE_SIZE = 50
const CACHE_PREFIX = 'costcards:list:v3:'
const CACHE_TTL_MS = 5 * 60 * 1000

export { PAGE_SIZE }

export function getCostCardsApiBase() {
  const base = config.api.baseUrl.replace(/\/$/, '')
  return `${base}/api/costcards`
}

function escapeRegex(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function deriveGrossProfitMarginPercent(card) {
  const cost = Number(card.totalCostsKesPerKg ?? card.computed?.totalCostsKesPerKg)
  const selling = Number(card.sellingPriceKesPerKg)
  if (!isFinite(cost) || cost === 0 || !isFinite(selling)) return null
  return Number((((selling - cost) / cost) * 100).toFixed(2))
}

function toSummary(card) {
  const stored = card.grossProfitMarginPercent
  const fromComputed = card.computed?.grossProfitMarginPercent
  const derived = deriveGrossProfitMarginPercent(card)
  const grossProfitMarginPercent =
    stored != null && stored !== 0
      ? stored
      : (fromComputed ?? derived ?? stored ?? null)

  return {
    _id: card._id,
    productName: card.productName || '',
    periodExact: card.periodExact || null,
    templateKey: card.templateKey,
    totalCostsKesPerKg: card.totalCostsKesPerKg ?? card.computed?.totalCostsKesPerKg ?? null,
    sellingPriceKesPerKg: card.sellingPriceKesPerKg ?? null,
    grossProfitMarginPercent,
    createdAt: card.createdAt || null
  }
}

function getDateValue(card) {
  const raw = card.periodExact || card.createdAt
  if (!raw) return 0
  const d = new Date(raw)
  return isNaN(d.getTime()) ? 0 : d.getTime()
}

function clientPaginate(cards, { page, limit, sort, search }) {
  const term = (search || '').trim().toLowerCase()
  let filtered = cards
  if (term) {
    filtered = cards.filter(c =>
      String(c.productName || '').toLowerCase().includes(term)
    )
  }

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'za') {
      return String(b.productName || '').localeCompare(String(a.productName || ''))
    }
    if (sort === 'period-newest') {
      return getDateValue(b) - getDateValue(a)
    }
    if (sort === 'period-oldest') {
      return getDateValue(a) - getDateValue(b)
    }
    return String(a.productName || '').localeCompare(String(b.productName || ''))
  })

  const total = sorted.length
  const totalPages = Math.ceil(total / limit) || 0
  const start = (page - 1) * limit
  const data = sorted.slice(start, start + limit)

  return { data, page, limit, total, totalPages, legacy: true }
}

export function normalizeListResponse(result, params) {
  const { page, limit, sort, search } = params

  if (Array.isArray(result)) {
    const summaries = result.map(toSummary)
    return clientPaginate(summaries, { page, limit, sort, search })
  }

  if (result && Array.isArray(result.data)) {
    return {
      data: result.data.map(toSummary),
      page: result.page ?? page,
      limit: result.limit ?? limit,
      total: result.total ?? result.data.length,
      totalPages: result.totalPages ?? 1,
      legacy: false
    }
  }

  return { data: [], page, limit, total: 0, totalPages: 0, legacy: false }
}

export function buildListUrl({ page, limit, sort, search, product }) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sort
  })
  const term = (search || product || '').trim()
  if (term) {
    params.set('search', term)
  }
  return `${getCostCardsApiBase()}?${params.toString()}`
}

function cacheKey(params) {
  return `${CACHE_PREFIX}${params.page}:${params.limit}:${params.sort}:${params.search || ''}:${params.product || ''}`
}

export function readListCache(params) {
  try {
    const raw = sessionStorage.getItem(cacheKey(params))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || Date.now() - parsed.storedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(cacheKey(params))
      return null
    }
    return parsed.result
  } catch {
    return null
  }
}

export function writeListCache(params, result) {
  try {
    sessionStorage.setItem(cacheKey(params), JSON.stringify({
      storedAt: Date.now(),
      result
    }))
  } catch {
    // Ignore quota errors
  }
}

export async function fetchCostCardsList(params, { signal } = {}) {
  const url = buildListUrl(params)
  const res = await fetch(url, { method: 'GET', signal })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `Error ${res.status}`)
  }
  const json = await res.json()
  const normalized = normalizeListResponse(json, params)
  writeListCache(params, normalized)
  return normalized
}

let prefetchPromise = null

export function prefetchCostCardsList(params = { page: 1, limit: PAGE_SIZE, sort: 'az', search: '', product: '' }) {
  const cached = readListCache(params)
  if (cached) return Promise.resolve(cached)
  if (prefetchPromise) return prefetchPromise

  prefetchPromise = fetchCostCardsList(params)
    .catch(() => null)
    .finally(() => {
      prefetchPromise = null
    })

  return prefetchPromise
}
